import gql from 'graphql-tag';
import {flowRight as compose} from 'lodash';
import PropTypes from 'prop-types';
import React, {useEffect, useState} from 'react';
import {Mutation, useQuery, withApollo} from 'react-apollo';
import {Col, Container, Row} from 'react-bootstrap';
import {withTranslation} from 'react-i18next';
import {withRouter} from 'react-router';
import {PulseLoader} from 'react-spinners';
import styled from 'styled-components';
import {
  checkErrorType,
  dateFormatFR,
} from '../../../../utils';
import {
  buildFormToSubmit,
  formStateWithData,
  initFormState,
  validateFormState,
} from '../../../../utils/form';
import FormInputFactory from '../../../../common/components/layout/FormInputFactory';
import InputCol from '../../../../common/components/layout/InputCol';
import {CancelButton, HeadDiv, TitleButton} from '../../../../common/components/StyledComp';
import {ERROR_TYPE_EMAIL_ALREADY_USED} from '../../../../common/constants';
import DisplayBrandWithSelect from '../../../brand/components/DisplayBrandWithSelect';

const GRAPHQL_QUERY_CENTRAL_PURCHASING = gql`
  query centralPurchasing($id: ID!) {
    centralPurchasing(id: $id) {
      id
      brand {
        id
        name
      }
      user {
        id
        firstName
        lastName
        email
        postalCode
        street
        city
      }
      contactEmail
      contactName
      createdAt
      siret
    }
  }
`;

const GRAPHQL_MUTATION_UPDATE_CENTRAL_PURCHASING = gql`
  mutation updateCentralPurchasing($userInput: UpdateUserInput!, $centralPurchasingInput: UpdateCentralPurchasingInput!) {
    updateCentralPurchasing(userInput: $userInput, centralPurchasingInput: $centralPurchasingInput) {
      id
      firstName
      lastName
      isActive
      isDeleted
      email
      postalCode
      street
      city
      centralPurchasing{
        id
        brand {
          id
          name
        }
        contactEmail
        contactName
        createdAt
        siret
      }
    }
  }
`;

const CancelButtonForm = styled(CancelButton)`
  margin-top: 0;
  height: auto;
  width: auto;
`;

const formFactory = {
  userId: {
    initial: 0,
    fromData: 'centralPurchasing.user.id',
    toSubmit: 'userInput.id',
    required: true,
    type: 'number',
  },
  firstName: {
    initial: '',
    fromData: 'centralPurchasing.user.firstName',
    toSubmit: 'userInput.firstName',
    required: true,
    type: 'text',
  },
  lastName: {
    initial: '',
    fromData: 'centralPurchasing.user.lastName',
    toSubmit: 'userInput.lastName',
    required: true,
    type: 'text',
  },
  email: {
    initial: '',
    fromData: 'centralPurchasing.user.email',
    toSubmit: 'userInput.email',
    required: true,
    type: 'email',
  },
  brandId: {
    initial: 0,
    fromData: 'centralPurchasing.brand.id',
    toSubmit: 'centralPurchasingInput.brandId',
    required: true,
    type: 'number',
  },
  street: {
    initial: '',
    fromData: 'centralPurchasing.user.street',
    toSubmit: 'userInput.street',
    required: true,
    type: 'text',
  },
  postalCode: {
    initial: '',
    fromData: 'centralPurchasing.user.postalCode',
    toSubmit: 'userInput.postalCode',
    required: true,
    type: 'text',
  },
  city: {
    initial: '',
    fromData: 'centralPurchasing.user.city',
    toSubmit: 'userInput.city',
    required: true,
    type: 'text',
  },
  contactEmail: {
    initial: '',
    fromData: 'centralPurchasing.contactEmail',
    toSubmit: 'centralPurchasingInput.contactEmail',
    required: true,
    type: 'email',
  },
  contactName: {
    initial: '',
    fromData: 'centralPurchasing.contactName',
    toSubmit: 'centralPurchasingInput.contactName',
    required: true,
    type: 'text',
  },
  siret: {
    initial: '',
    fromData: 'centralPurchasing.siret',
    toSubmit: 'centralPurchasingInput.siret',
    required: true,
    type: 'text',
  },
  createdAt: {
    initial: '',
    fromData: 'centralPurchasing.createdAt',
    toSubmit: false,
    required: false,
    type: 'text',
  },
};

const initialState = initFormState(formFactory);

const CentralDetails = ({match, t, history}) => {
  const centralID = match.params.id || null;
  const [centralValues, setCentralValues] = useState(initialState);

  const setState = (name, value) => {
    setCentralValues({
      ...centralValues,
      [name]: formFactory[name].type === 'number' ? +value : value,
    });
  };

  const {loading, error, data} = useQuery(GRAPHQL_QUERY_CENTRAL_PURCHASING, {
    variables: {id: centralID},
  });

  useEffect(() => {
    if (data && !loading) {
      const newStateFilled = formStateWithData(data, formFactory);
      setCentralValues(newStateFilled);
    }
  }, [data, loading]);

  const submitChanges = (e, updateCentralPurchasing) => {
    e.preventDefault();
    if (validateFormState(formFactory, centralValues)) {

      const variables = buildFormToSubmit(formFactory, centralValues);
      updateCentralPurchasing({
        variables,
      })
        .catch(error => {
          console.error(t('failed') + error);
        });
    }
  };
  if (loading) {
    return <PulseLoader/>;
  } else if (error || !data.centralPurchasing) {
    return <h4>{t('noCentralPurchasing')}</h4>;
  } else {
    return (
      <Mutation
        mutation={GRAPHQL_MUTATION_UPDATE_CENTRAL_PURCHASING}
        variables={centralValues}
        onCompleted={() => history.push('/admin/centrals')}
      >
        {(updateCentralPurchasing, {error, loading: mutationLoading}) => {
          const existingEmail = checkErrorType(error, ERROR_TYPE_EMAIL_ALREADY_USED);
          if (error && !existingEmail) {
            console.error(error);
          }
          return (
            <>
              <form>
                <Container className="general-container">
                  <Row className="info-row">
                    <Col xs={6}>
                      <Row className="section-info">
                        <HeadDiv className="info-header">{`${t('AddCentralPurchasing.personalInfos')} : `}</HeadDiv>
                        <FormInputFactory
                          nameInput="lastName"
                          formFactory={formFactory}
                          formState={centralValues}
                          onChange={setState}
                        />
                        <FormInputFactory
                          nameInput="firstName"
                          formFactory={formFactory}
                          formState={centralValues}
                          onChange={setState}
                        />
                        <FormInputFactory
                          nameInput="email"
                          formFactory={formFactory}
                          formState={centralValues}
                          onChange={setState}
                          translationValue="personalEmail"
                          customText={existingEmail ? t('existingEmail') : t('wrongMail')}
                        />
                      </Row>
                    </Col>
                    <Col xs={6}>
                      <DisplayBrandWithSelect
                        onChange={setState}
                        values={centralValues}
                      />
                    </Col>
                  </Row>
                  <Row className="info-row">
                    <Col xs={12}>
                      <Row className="section-info">
                        <HeadDiv className="info-header">{`${t('centralPurchasingInfo')} : `}</HeadDiv>
                        <FormInputFactory
                          nameInput="street"
                          formFactory={formFactory}
                          formState={centralValues}
                          onChange={setState}
                          size={6}
                        />
                        <FormInputFactory
                          nameInput="siret"
                          formFactory={formFactory}
                          formState={centralValues}
                          onChange={setState}
                          size={6}
                        />
                        <Col xs={6}>
                          <Row>
                            <FormInputFactory
                              nameInput="postalCode"
                              formFactory={formFactory}
                              formState={centralValues}
                              onChange={setState}
                              size={3}
                            />
                            <FormInputFactory
                              nameInput="city"
                              formFactory={formFactory}
                              formState={centralValues}
                              onChange={setState}
                              size={9}
                            />
                          </Row>
                        </Col>
                        <FormInputFactory
                          nameInput="contactEmail"
                          formFactory={formFactory}
                          formState={centralValues}
                          onChange={setState}
                          customText={t('wrongMail')}
                          size={6}
                        />
                        <InputCol
                          title={`${t('creation')} : `}
                          size={6}
                          type="number"
                          name="createdAt"
                          disabled
                          placeholder={dateFormatFR({date: centralValues.createdAt})}
                        />
                      </Row>
                    </Col>
                  </Row>
                </Container>
                <div className="popin-buttons">
                  <div>
                    <TitleButton
                      onClick={e => submitChanges(e, updateCentralPurchasing)}
                      disabled={mutationLoading}
                    >
                      {t('save')}
                    </TitleButton>
                  </div>
                  <CancelButtonForm
                    onClick={() => history.push('/admin/centrals')}>{t('cancel')}</CancelButtonForm>
                </div>
              </form>
            </>
          );
        }}
      </Mutation>
    );
  }
};

CentralDetails.propTypes = {
  t: PropTypes.func.isRequired,
  match: PropTypes.object.isRequired,
  client: PropTypes.object.isRequired,
  history: PropTypes.any.isRequired,
};

export default compose(
  withTranslation('centralPurchasing'),
  withRouter,
  withApollo,
)(CentralDetails);
